<template>
    <div class="cart-line-offers-tab modl-tab-body">
        <div class="modl-body">
            <OfferSelector :initial-selected-offers="selectedOffers"
                           :offers="offers"
                           :disabled-offer-ids="disabledOfferIds"
                           :variable-discounts-collection="variableDiscountsCollection"
                           @update:selected-offers="updateSelectedOffers">
                <!-- The contents of this slot are displayed inside the OfferSelector component
                when permission is granted. -->
                <div class="alert alert-info">
                    This discount will be applied to <strong>{{ cartLineName }}</strong> only.
                    To apply a discount to the whole cart use the <strong>Discounts</strong> button at the bottom of the
                    cart.
                </div>
            </OfferSelector>
        </div>

        <div class="modl-footer">
            <!-- Cancel button -->
            <SquareBtn size="lg"
                       class="cancel-btn"
                       @click="$emit('cancel')">
                {{ l(cancelLabel) }}
            </SquareBtn>

            <!-- OK button -->
            <SquareBtn :loading="saving"
                       class="ok-btn"
                       color="primary"
                       size="lg"
                       @click="submit">
                {{ l('SAVE') }}
            </SquareBtn>
        </div>
    </div>
</template>

<script>
import OfferSelector from '@/epos/vue/modals/CartOffers/OfferSelector';
import SquareBtn from '@/vue/components/SquareBtn';
import VariableDiscountsCollection from '@/models/VariableDiscountsCollection';
import { CartLineDetailsTaxMixin } from '@/epos/vue/modals/CartLineDetails/CartLineDetailsTaxMixin';
import { cloneDeep } from '@/functions/clone';
import { ConfirmTabAwayMixin } from '@/vue/mixins/ConfirmTabAwayMixin';
import { container } from '@/container';

export default {
    name: 'CartLineOffersTab',

    components: { SquareBtn, OfferSelector },

    mixins: [
        CartLineDetailsTaxMixin,
        ConfirmTabAwayMixin,
    ],

    emits: [
        'cancel',
        'resolve',
    ],

    data() {
        return {
            saving: false,

            /**
             * Keeps track of the currently selected offer, in the format needed by the OfferSelector component.
             * For line level offers there will only be one offer selected one time.
             *
             * @type {Object<number, number>}
             */
            selectedOffers: {},

            /**
             * All available offers. Populated upon creation.
             *
             * @type {Offer[]}
             */
            offers: [],

            /**
             * @type {VariableDiscountsCollection}
             */
            variableDiscountsCollection: this.cartLine.variableDiscountsCollection
                ? cloneDeep(this.cartLine.variableDiscountsCollection)
                : new VariableDiscountsCollection(),

            /**
             * Keeps track of the offer selected when the tab was loaded,
             * so that we can notify the user if they will change tab and lose their changes.
             *
             * @type {string}
             */
            initialSelectedOffersJson: '',

            /**
             * Keeps track of the variable discount data when the tab was loaded,
             * so that we can notify the user if they will change tab and lose their changes.
             *
             * @type {string}
             */
            initialVariableDiscountCollectionJson: '',
        };
    },

    computed: {
        /**
         * @returns {string}
         */
        cartLineName() {
            return this.cartLine.getName();
        },

        /**
         * Returns an array of line-level offer IDs that *do not* support the current cart line.
         *
         * @returns {Number[]}
         */
        disabledOfferIds() {
            return this.offers
                .filter((offer) => {
                    return !offer.supportsItem(
                        this.cartLine.getItemID(),
                        this.cartLine.getCategoryID(),
                        this.cartLine.getEventID()
                    );
                })
                .map((offer) => offer.ID);
        },
    },

    async created() {
        // Populate the selectedOffers object upon creation.
        // We only allow one offer at a time for line-level offers, so this object should only have one item
        // in it. But we are using the <OfferSelector> component which is also used for cart-level offers, and
        // that does support multiple offers. So we need to jimmy our data in the structure required by that component.
        this.selectedOffers = {};
        if (this.cartLine.manualOffer) {
            this.selectedOffers[this.cartLine.manualOffer.ID] = 1;
        }
        this.initialSelectedOffersJson = JSON.stringify(this.selectedOffers);
        this.initialVariableDiscountCollectionJson = JSON.stringify(this.variableDiscountsCollection);

        // Get all manually applied offers from the Offer Manager
        container.offerRepository.findAllManualLineOffers().then((offers) => {
            this.offers = offers;
        });
    },

    methods: {
        submit() {
            // Set the selected offer on the cart line. There will be zero or one offers selected.
            let selectedOffer = null;
            const selectedOfferIDs = Object.keys(this.selectedOffers);
            if (selectedOfferIDs.length > 0) {
                selectedOffer = this.offers.find((o) => o.ID === parseInt(selectedOfferIDs[0]));
            }

            this.cartLine.setVariableDiscountsCollection(this.variableDiscountsCollection);
            this.cartLine.setManualOffer(selectedOffer);

            this.$emit('resolve');
        },

        hasUnsavedChanges() {
            return this.initialSelectedOffersJson !== JSON.stringify(this.selectedOffers)
                || this.initialVariableDiscountCollectionJson !== JSON.stringify(this.variableDiscountsCollection);
        },

        updateSelectedOffers(newValue) {
            this.selectedOffers = newValue;
        },
    },
};
</script>
